<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DealsAPI from 'dashboard/api/deals';
import PipelinesAPI from 'dashboard/api/pipelines';
import ContactsAPI from 'dashboard/api/contacts';
import ConversationsAPI from 'dashboard/api/conversations';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useStore } from 'vuex';

const store = useStore();
const currentUserId = computed(() => store.getters.getCurrentUserID);
const { can } = usePermissions();
// Antes era un whitelist hardcoded por email; ahora respeta chat.view granular.
const canSeeChat = computed(() => can('chat.view'));

const route = useRoute();
const router = useRouter();

const deal = ref(null);
const pipeline = ref(null);
const conversation = ref(null);
const messages = ref([]);
const loading = ref(true);
const error = ref(null);
const stageId = ref(null);
const savingStage = ref(false);

const newMessage = ref('');
const sending = ref(false);
const messagesContainer = ref(null);

const dealId = computed(() => Number(route.params.dealId));
const accountId = computed(() => route.params.accountId);

const currentStage = computed(() =>
  pipeline.value?.stages?.find(s => s.id === deal.value?.pipeline_stage_id)
);

const stageIndex = computed(() => {
  if (!pipeline.value?.stages || !deal.value) return -1;
  return pipeline.value.stages.findIndex(
    s => s.id === deal.value.pipeline_stage_id
  );
});

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const d = new Date(timestamp * 1000);
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp * 1000);
  return d.toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('');
}

async function loadConversationMessages(convId) {
  try {
    const { data } = await ConversationsAPI.getMessages(convId);
    messages.value = data.payload || data || [];
    await nextTick();
    scrollToBottom();
  } catch (e) {
    // No bloquear la vista si falla la carga del chat
    messages.value = [];
  }
}

async function resolveConversation() {
  if (!deal.value) return;
  if (deal.value.conversation_id) {
    try {
      const { data } = await ConversationsAPI.show(deal.value.conversation_id);
      conversation.value = data;
    } catch (e) {
      conversation.value = { id: deal.value.conversation_id };
    }
    await loadConversationMessages(deal.value.conversation_id);
    return;
  }
  if (deal.value.contact_id) {
    try {
      const { data } = await ContactsAPI.getConversations(deal.value.contact_id);
      const list = data.payload || data || [];
      if (list.length > 0) {
        conversation.value = list[0];
        await loadConversationMessages(list[0].id);
      }
    } catch (e) {
      conversation.value = null;
    }
  }
}

// Escribir al cliente desde el CRM (Victor 2026-05-16): si el lead no tiene
// conversación vinculada, la crea en la bandeja asociada al contacto y abre el
// chat. Si el cliente ya escribió, Chatwoot deduplica por teléfono y queda en
// el mismo contacto.
const creatingConv = ref(false);
async function startConversation() {
  if (conversation.value?.id) {
    openConversationInChatwoot();
    return;
  }
  if (!deal.value?.contact_id) return;
  creatingConv.value = true;
  try {
    const { data: ci } = await ContactsAPI.getContactableInboxes(
      deal.value.contact_id
    );
    const list = ci.payload || ci || [];
    if (!list.length) {
      // eslint-disable-next-line no-alert
      alert('El contacto no tiene bandeja asociada para escribirle.');
      return;
    }
    const pick =
      list.find(x => /whatsapp|api/i.test(x.inbox?.channel_type || '')) ||
      list[0];
    const { data: conv } = await ConversationsAPI.create({
      source_id: pick.source_id,
      inbox_id: pick.inbox?.id || pick.inbox_id,
      contact_id: deal.value.contact_id,
      // Asignármela a mí (quien inicia el chat) para verla y poder escribir
      // pese a la regla "agentes solo ven lo asignado" (Victor 2026-05-16).
      assignee_id: currentUserId.value,
    });
    conversation.value = conv;
    router.push(
      `/app/accounts/${accountId.value}/conversations/${conv.id}`
    );
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert(
      'No se pudo iniciar la conversación: ' +
        (e?.response?.data?.message || e?.message || 'error')
    );
  } finally {
    creatingConv.value = false;
  }
}

async function loadDeal() {
  loading.value = true;
  error.value = null;
  try {
    const { data: dealData } = await DealsAPI.show(dealId.value);
    deal.value = dealData;
    stageId.value = dealData.pipeline_stage_id;
    const { data: pipelineData } = await PipelinesAPI.show(dealData.pipeline_id);
    pipeline.value = pipelineData;
    await resolveConversation();
  } catch (e) {
    error.value = e.response?.status ? `HTTP ${e.response.status}` : e.message;
  } finally {
    loading.value = false;
  }
}

function scrollToBottom() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
}

async function sendMessage() {
  const content = newMessage.value.trim();
  if (!content || !conversation.value?.id || sending.value) return;
  sending.value = true;
  try {
    const { data } = await ConversationsAPI.sendMessage(conversation.value.id, {
      content,
    });
    messages.value.push(data);
    newMessage.value = '';
    await nextTick();
    scrollToBottom();
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error enviando mensaje: ' + (e.response?.data?.message || e.message));
  } finally {
    sending.value = false;
  }
}

async function changeStage(targetStageId) {
  if (!deal.value || targetStageId === deal.value.pipeline_stage_id) return;
  savingStage.value = true;
  const previousStageId = deal.value.pipeline_stage_id;
  deal.value.pipeline_stage_id = targetStageId;
  stageId.value = targetStageId;
  try {
    const { data } = await DealsAPI.moveToStage(deal.value.id, targetStageId);
    deal.value = data;
  } catch (e) {
    deal.value.pipeline_stage_id = previousStageId;
    stageId.value = previousStageId;
    // eslint-disable-next-line no-alert
    alert('Error cambiando etapa: ' + (e.response?.data?.message || e.message));
  } finally {
    savingStage.value = false;
  }
}

function goBackToKanban() {
  router.push({
    name: 'pipelines_view',
    params: {
      accountId: accountId.value,
      pipelineId: deal.value?.pipeline_id,
    },
  });
}

function openContactInChatwoot() {
  if (!deal.value?.contact_id) return;
  router.push(`/app/accounts/${accountId.value}/contacts/${deal.value.contact_id}`);
}

function openConversationInChatwoot() {
  if (!conversation.value?.id) return;
  router.push(`/app/accounts/${accountId.value}/conversations/${conversation.value.id}`);
}

function isIncoming(message) {
  return message.message_type === MESSAGE_TYPE.INCOMING;
}

function isOutgoing(message) {
  return message.message_type === MESSAGE_TYPE.OUTGOING;
}

function isActivity(message) {
  return message.message_type === MESSAGE_TYPE.ACTIVITY;
}

onMounted(loadDeal);
watch(dealId, loadDeal);
</script>

<template>
  <div class="flex h-full bg-slate-50 dark:bg-slate-950 overflow-hidden">
    <div v-if="loading" class="flex-1 flex items-center justify-center text-sm text-slate-500">
      Cargando lead…
    </div>

    <div
      v-else-if="error"
      class="flex-1 flex items-center justify-center"
    >
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-300 text-sm max-w-md">
        Error al cargar lead: {{ error }}
        <button
          @click="goBackToKanban"
          class="block mt-3 text-xs underline"
        >← Volver al kanban</button>
      </div>
    </div>

    <template v-else-if="deal">
      <aside class="w-[380px] shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col">
        <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-start gap-3">
          <button
            @click="goBackToKanban"
            class="text-slate-400 hover:text-slate-900 dark:hover:text-white text-lg leading-none mt-1"
            title="Volver al kanban"
          >←</button>
          <template v-if="canSeeChat">
            <img
              v-if="deal.contact?.thumbnail"
              :src="deal.contact.thumbnail"
              class="w-10 h-10 rounded-full object-cover"
              :alt="deal.contact.name"
            />
            <div
              v-else
              class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center font-bold text-sm"
            >
              {{ getInitials(deal.contact?.name || deal.title) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-bold truncate text-slate-900 dark:text-slate-100">
                {{ deal.contact?.name || deal.title || 'Sin nombre' }}
              </div>
              <div class="text-[11px] text-slate-500 truncate">
                {{ deal.contact?.phone_number || '—' }}
              </div>
            </div>
            <button
              v-if="deal.contact_id"
              @click="openContactInChatwoot"
              class="text-slate-400 hover:text-blue-500 text-xs"
              title="Ver contacto"
            >⚙️</button>
          </template>
          <template v-else>
            <div class="w-10 h-10 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center text-slate-500 dark:text-slate-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-bold truncate text-slate-900 dark:text-slate-100">
                Lead #{{ deal.id }}
              </div>
              <div class="text-[11px] text-slate-500 truncate">—</div>
            </div>
          </template>
        </div>

        <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            {{ pipeline?.name || 'Embudo' }}
          </div>
          <select
            v-model="stageId"
            :disabled="savingStage"
            @change="changeStage(stageId)"
            class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-xs text-slate-900 dark:text-slate-100"
          >
            <option v-for="s in pipeline?.stages || []" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
          <div class="flex gap-0.5 mt-2">
            <div
              v-for="(s, idx) in pipeline?.stages || []"
              :key="s.id"
              class="flex-1 h-1.5 transition-colors"
              :class="[
                idx === 0 ? 'rounded-l-full' : '',
                idx === (pipeline?.stages?.length || 0) - 1 ? 'rounded-r-full' : '',
                idx < stageIndex ? 'bg-emerald-500' : '',
                idx === stageIndex ? '' : '',
                idx > stageIndex ? 'bg-slate-300 dark:bg-slate-700' : '',
              ]"
              :style="idx === stageIndex ? { background: s.color } : {}"
            />
          </div>
          <div class="text-[10px] text-slate-400 mt-1.5 truncate">
            {{ currentStage?.name || '—' }} · {{ deal.days_in_stage || 0 }}d en columna
          </div>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">📋 Datos</div>
            <div class="space-y-2 text-xs">
              <div class="flex justify-between gap-2">
                <span class="text-slate-500 shrink-0">Título</span>
                <span class="text-slate-900 dark:text-slate-200 truncate text-right">{{ deal.title || '—' }}</span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-slate-500 shrink-0">Valor</span>
                <span class="text-emerald-600 dark:text-emerald-400 font-medium">
                  {{ deal.value ? `${deal.currency || 'DOP'} ${Number(deal.value).toLocaleString('es-DO')}` : '—' }}
                </span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-slate-500 shrink-0">Responsable</span>
                <span class="text-slate-900 dark:text-slate-200 truncate text-right">
                  {{ deal.assignee?.name || 'Sin asignar' }}
                </span>
              </div>
              <div class="flex justify-between gap-2">
                <span class="text-slate-500 shrink-0">Creado</span>
                <span class="text-slate-900 dark:text-slate-200">{{ formatDate(deal.created_at) }}</span>
              </div>
            </div>
          </div>

          <div v-if="canSeeChat" class="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">📞 Contacto</div>
            <div class="space-y-1.5 text-xs">
              <div v-if="deal.contact?.phone_number" class="flex items-center gap-2">
                <span>📱</span>
                <span class="text-slate-900 dark:text-slate-200 truncate">{{ deal.contact.phone_number }}</span>
              </div>
              <div v-if="conversation?.id" class="flex items-center gap-2 text-slate-500">
                <span>💬</span>
                <button
                  @click="openConversationInChatwoot"
                  class="hover:text-blue-500 hover:underline"
                >Conversación #{{ conversation.id }}</button>
              </div>
              <div v-else class="text-slate-400 italic">Sin conversación vinculada</div>
            </div>
          </div>

          <div v-if="canSeeChat" class="px-4 py-3">
            <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">⚡ Acciones</div>
            <div class="grid grid-cols-2 gap-1.5">
              <button
                @click="openContactInChatwoot"
                class="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-[11px] py-1.5 rounded text-slate-700 dark:text-slate-200"
              >👤 Ver contacto</button>
              <button
                v-if="conversation?.id"
                @click="openConversationInChatwoot"
                class="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-[11px] py-1.5 rounded text-slate-700 dark:text-slate-200"
              >💬 Abrir chat</button>
              <button
                v-else
                :disabled="creatingConv"
                @click="startConversation"
                class="col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] py-1.5 rounded font-medium disabled:opacity-50"
              >{{ creatingConv ? 'Creando…' : '✍️ Escribir al cliente' }}</button>
            </div>
          </div>
        </div>
      </aside>

      <main v-if="!canSeeChat" class="flex-1 flex items-center justify-center bg-slate-100 dark:bg-slate-950 min-w-0">
        <div class="text-center px-6">
          <div class="text-5xl mb-3">🔒</div>
          <div class="text-base font-semibold text-slate-700 dark:text-slate-200">Bandeja restringida</div>
          <div class="text-sm text-slate-500 dark:text-slate-400 mt-1">No tienes acceso a esta conversación.</div>
        </div>
      </main>
      <main v-else class="flex-1 flex flex-col bg-slate-100 dark:bg-slate-950 min-w-0">
        <div class="px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 flex items-center gap-2 text-xs shrink-0">
          <span class="w-2 h-2 rounded-full" :class="conversation ? 'bg-emerald-400' : 'bg-slate-400'" />
          <span class="text-slate-500">
            {{ conversation ? `Conversación · ${conversation.inbox?.name || conversation.meta?.channel || 'Chat'}` : 'Sin conversación' }}
          </span>
          <span v-if="conversation" class="text-slate-400 ml-auto">{{ messages.length }} mensajes</span>
        </div>

        <div
          ref="messagesContainer"
          class="flex-1 overflow-y-auto p-4 space-y-2"
        >
          <div
            v-if="!conversation"
            class="h-full flex items-center justify-center text-center text-sm text-slate-400"
          >
            <div>
              <div class="text-4xl mb-2">💬</div>
              Este lead aún no tiene conversación vinculada
              <div class="mt-3">
                <button
                  :disabled="creatingConv"
                  @click="startConversation"
                  class="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 rounded font-medium disabled:opacity-50"
                >{{ creatingConv ? 'Creando…' : '✍️ Escribir al cliente' }}</button>
              </div>
            </div>
          </div>

          <div
            v-else-if="messages.length === 0"
            class="h-full flex items-center justify-center text-sm text-slate-400"
          >
            Cargando mensajes…
          </div>

          <template v-else>
            <div
              v-for="msg in messages"
              :key="msg.id"
              class="flex"
              :class="[
                isOutgoing(msg) ? 'justify-end' : '',
                isActivity(msg) ? 'justify-center' : '',
              ]"
            >
              <div
                v-if="isActivity(msg)"
                class="text-[10px] text-slate-400 italic px-2 py-1"
              >{{ msg.content }}</div>

              <div
                v-else
                class="rounded-lg px-3 py-2 max-w-md text-sm"
                :class="isOutgoing(msg)
                  ? 'bg-blue-600 text-white rounded-br-none'
                  : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-none border border-slate-200 dark:border-slate-700'"
              >
                <div v-if="msg.content" class="whitespace-pre-wrap break-words">{{ msg.content }}</div>
                <div
                  v-if="msg.attachments?.length"
                  class="mt-1 space-y-1"
                >
                  <div
                    v-for="att in msg.attachments"
                    :key="att.id"
                    class="text-[11px]"
                    :class="isOutgoing(msg) ? 'text-blue-100' : 'text-slate-500'"
                  >
                    📎 {{ att.file_type || 'archivo' }}
                  </div>
                </div>
                <div
                  class="text-[10px] mt-1 opacity-70"
                  :class="isOutgoing(msg) ? 'text-blue-100 text-right' : 'text-slate-400'"
                >{{ formatTime(msg.created_at) }}</div>
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="conversation"
          class="px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 shrink-0"
        >
          <div class="flex items-end gap-2">
            <textarea
              v-model="newMessage"
              @keydown.enter.exact.prevent="sendMessage"
              :disabled="sending"
              rows="2"
              placeholder="Escribe aquí tu mensaje…"
              class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-sm text-slate-900 dark:text-slate-100 resize-none focus:outline-none focus:border-blue-500"
            />
            <button
              @click="sendMessage"
              :disabled="sending || !newMessage.trim()"
              class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded px-4 py-2 text-sm font-medium"
            >
              {{ sending ? '…' : 'Enviar' }}
            </button>
          </div>
        </div>
      </main>
    </template>
  </div>
</template>
